/**
 * MediaEditor
 * ──────────────────────────────────────────────────────────────────────────────
 * Componente reutilizável de edição de mídia.
 * Recebe um File (imagem ou vídeo), exibe o editor e devolve o File processado
 * via onConfirm.
 *
 * Uso mínimo:
 *   <MediaEditor file={file} onConfirm={(edited) => upload(edited)} onCancel={() => {}} />
 *
 * Props opcionais:
 *   - tabs         → quais abas mostrar  (default: todas)
 *   - aspectRatio  → forçar aspect ratio no crop (default: livre)
 *   - footer       → slot para conteúdo extra no rodapé (ex: seletor de privacidade)
 *   - confirmLabel → rótulo do botão de confirmar (default: "Confirmar")
 * ──────────────────────────────────────────────────────────────────────────────
 */

import React, { useEffect, useRef, useState } from "react";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
  PercentCrop,
} from "react-image-crop";
import { Check, Scissors, Sliders, SlidersHorizontal, Type, Volume2, VolumeX, X } from "lucide-react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import "react-image-crop/dist/ReactCrop.css";
import Spinner from "../global-components/src/components/spinner/spiner";
import "./media-editor.css"
import { getNativeKeyboardHeight, getNativeNavigationHeight, isNewAppAndroid } from "../utils/platform";
import { isIOSWebView } from "../global-components/src/utils/platform";
import { useTranslation } from 'react-i18next';

// ─── Tipos públicos ────────────────────────────────────────────────────────────

export type MediaEditorTab = "filtros" | "ajustes" | "texto" | "trim" | "audio";

export interface MediaEditorProps {
  /** Arquivo a editar. Mude esta prop para trocar a mídia sem remontar. */
  file: File | null;
  /** Chamado com o arquivo processado ao confirmar. */
  onConfirm: (file: File) => void | Promise<void>;
  /** Chamado ao fechar/cancelar sem confirmar. */
  onCancel: () => void;
  /** Abas disponíveis. Omita para mostrar todas pertinentes ao tipo de mídia. */
  tabs?: MediaEditorTab[];
  /** Aspect ratio do crop de imagem (ex: 1 para quadrado, 16/9 para widescreen). */
  aspectRatio?: number;
  /** Slot renderizado à esquerda do botão de confirmar no rodapé. */
  footer?: React.ReactNode;
  /** Rótulo do botão confirmar. */
  confirmLabel?: React.ReactNode;
  /** Dimensão mínima em px para imagens (default: 150). */
  minDimension?: number;
}

// ─── Tipos internos ────────────────────────────────────────────────────────────

type ActiveTab = MediaEditorTab;

interface FilterDef {
  id: string;
  label: string;
  css: string;
  swatch: string;
}

interface Adjustments {
  brightness: number;
  contrast: number;
  saturation: number;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const TEXT_COLORS = ["#ffffff", "#f87171", "#60a5fa", "#34d399", "#fbbf24", "#e879f9"];

const FILTERS: FilterDef[] = [
  { id: "normal",  label: "Normal",  css: "none",                                             swatch: "#1d2d44" },
  { id: "lavanda", label: "Lavanda", css: "hue-rotate(270deg) saturate(1.4)",                  swatch: "#b3a4e0" },
  { id: "quente",  label: "Quente",  css: "sepia(0.5) saturate(1.3) hue-rotate(-15deg)",        swatch: "#e0c4a4" },
  { id: "frio",    label: "Frio",    css: "saturate(0.8) hue-rotate(200deg) brightness(1.05)", swatch: "#a0bce0" },
  { id: "pb",      label: "P&B",     css: "grayscale(1)",                                      swatch: "#888"   },
  { id: "vintage", label: "Vintage", css: "sepia(0.8) contrast(0.9) brightness(0.9)",          swatch: "#c4a882" },
  { id: "neon",    label: "Neon",    css: "saturate(2.5) contrast(1.2) brightness(1.1)",       swatch: "#3d1a78" },
];

const DEFAULT_ADJ: Adjustments = { brightness: 100, contrast: 100, saturation: 100 };

const IMAGE_TOOLS: MediaEditorTab[] = ["filtros", "ajustes", "texto"];
const VIDEO_TOOLS: MediaEditorTab[] = ["trim", "audio", "texto"];

const FFMPEG_BASE = "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const adjToCSS = (adj: Adjustments, filterCss: string) => {
  const base = `brightness(${adj.brightness}%) contrast(${adj.contrast}%) saturate(${adj.saturation}%)`;
  return filterCss === "none" ? base : `${base} ${filterCss}`;
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const fmt = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// ─── Componente ───────────────────────────────────────────────────────────────

const MediaEditor: React.FC<MediaEditorProps> = ({
  file,
  onConfirm,
  onCancel,
  tabs,
  aspectRatio,
  footer,
  confirmLabel,
  minDimension = 150,
}) => {
  // ── Tipo de mídia detectado do file ──────────────────────────────────────
  const mediaType = !file
    ? null
    : file.type.startsWith("video/")
    ? "video"
    : "image";

  // ── Refs ──────────────────────────────────────────────────────────────────
  const imgRef         = useRef<HTMLImageElement | null>(null);
  const canvasRef      = useRef<HTMLCanvasElement | null>(null);
  const videoRef       = useRef<HTMLVideoElement | null>(null);
  const trimTrackRef   = useRef<HTMLDivElement | null>(null);
  const ffmpegRef      = useRef<FFmpeg | null>(null);
  const ffmpegReady    = useRef(false);
  const inputRef  = useRef<HTMLInputElement>(null);
  const objectUrlRef   = useRef<string>("");          // para revogar ao desmontar

  // ── Estado de mídia ──────────────────────────────────────────────────────
  const [imgSrc,  setImgSrc]  = useState("");
  const [vidSrc,  setVidSrc]  = useState("");
  const [crop,    setCrop]    = useState<PercentCrop | undefined>(undefined);
  const [cropped, setCropped] = useState(false);
  const { t } = useTranslation();
  const [bottomPadding, setBottomPadding] = useState(10);

  // ── Estado de edição ─────────────────────────────────────────────────────
  const [activeTool,   setActiveTool]   = useState<ActiveTab | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterDef>(FILTERS[0]);
  const [adj,          setAdj]          = useState<Adjustments>(DEFAULT_ADJ);
  const [textValue,    setTextValue]    = useState("");
  const [textColor,    setTextColor]    = useState(TEXT_COLORS[0]);
  const [textBold,     setTextBold]     = useState(false);
  const [cropPreviewSrc, setCropPreviewSrc] = useState("");

  // ── Estado de vídeo ──────────────────────────────────────────────────────
  const [duration,    setDuration]    = useState(0);
  const [trimStart,   setTrimStart]   = useState(0);
  const [trimEnd,     setTrimEnd]     = useState(1);
  const [playing,     setPlaying]     = useState(false);
  const [muted,       setMuted]       = useState(false);
  const [volume,      setVolume]      = useState(100);
  const [currentTime, setCurrentTime] = useState(0);

  // ── Estado de loading ─────────────────────────────────────────────────────
  const [confirming,   setConfirming]   = useState(false);
  const [trimProgress, setTrimProgress] = useState<number | null>(null);
  const [error,        setError]        = useState("");

  // ─────────────────────────────────────────────────────────────────────────
  // Efeito: carrega nova mídia sempre que `file` mudar
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Limpa estado anterior
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    setImgSrc(""); setVidSrc(""); setCrop(undefined); setCropped(false);
    setActiveFilter(FILTERS[0]); setAdj(DEFAULT_ADJ);
    setTextValue(""); setTextColor(TEXT_COLORS[0]); setTextBold(false);
    setTrimStart(0); setTrimEnd(1); setPlaying(false); setMuted(false);
    setVolume(100); setDuration(0); setCurrentTime(0);
    setError(""); setTrimProgress(null);
    setActiveTool(null); setCropPreviewSrc("");

    if (!file) return;

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        const url = reader.result?.toString() ?? "";
        const img = new Image();
        img.src = url;
        img.onload = () => {
          if (img.naturalWidth < minDimension || img.naturalHeight < minDimension) {
            setError(`A imagem precisa ter pelo menos ${minDimension} × ${minDimension} px.`);
            return;
          }
          setImgSrc(url);
        };
      };
      reader.readAsDataURL(file);
      return;
    }

    if (file.type.startsWith("video/")) {
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setVidSrc(url);
    }

    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [file]);

  useEffect(() => {
      const handleKeyboardHeightChange = () => {
          const heightKeyboard = getNativeKeyboardHeight();
          const nativeNavigationHeight = getNativeNavigationHeight() ?? 0;
          let height = 30;
          if (inputRef.current) height = inputRef?.current.clientHeight;

          setBottomPadding(heightKeyboard ? (heightKeyboard + nativeNavigationHeight) : (nativeNavigationHeight + 10));
      };

      window.addEventListener("keyboardHeightChange", handleKeyboardHeightChange);

      return () => {
          window.removeEventListener("keyboardHeightChange", handleKeyboardHeightChange);
      };
  }, []);

  
  function ajustaNewApp() {
      if((isNewAppAndroid() && isNewAppAndroid()) || isIOSWebView()){
          const nativeNavigationHeight = getNativeNavigationHeight();
          let height = 30;
          if (inputRef.current) height = inputRef?.current.clientHeight;

          if(nativeNavigationHeight && nativeNavigationHeight > 0){
              setBottomPadding(nativeNavigationHeight);
          }

          window.addEventListener("navigationBarHeightChange", ajustaNewApp);
          return () => window.removeEventListener("navigationBarHeightChange", ajustaNewApp);
      }
  }
  useEffect(() => {
      ajustaNewApp()
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Crop
  // ─────────────────────────────────────────────────────────────────────────

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (activeTool !== "filtros" && activeTool !== null) return; // don't reset crop when showing preview
    if (activeTool === null) return; // only init crop when crop tool is active
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const ar = aspectRatio ?? 0;

    const c = ar
      ? makeAspectCrop({ unit: "%", width: 90 }, ar, naturalWidth, naturalHeight)
      : { unit: "%" as const, width: 100, height: 100, x: 0, y: 0 };

    const centered = ar
      ? centerCrop(c as any, naturalWidth, naturalHeight)
      : c;

    setCrop(centered as PercentCrop);
  };

  const onCropImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const ar = aspectRatio ?? 0;
    if (!crop) {
      const c = ar
        ? makeAspectCrop({ unit: "%", width: 90 }, ar, naturalWidth, naturalHeight)
        : { unit: "%" as const, width: 100, height: 100, x: 0, y: 0 };
      const centered = ar ? centerCrop(c as any, naturalWidth, naturalHeight) : c;
      setCrop(centered as PercentCrop);
    }
  };

  const commitCropPreview = (completedCrop: PercentCrop) => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    const pixelCrop = convertToPixelCrop(completedCrop, img.naturalWidth, img.naturalHeight);
    canvas.width = Math.round(pixelCrop.width);
    canvas.height = Math.round(pixelCrop.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
    setCropPreviewSrc(canvas.toDataURL("image/jpeg", 0.92));
    setCrop(completedCrop);
    setCropped(true);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Trim drag
  // ─────────────────────────────────────────────────────────────────────────

  const startTrimDrag = (handle: "start" | "end") => (e: React.MouseEvent) => {
    e.preventDefault();
    const track = trimTrackRef.current;
    if (!track) return;
    const onMove = (ev: MouseEvent) => {
      const rect = track.getBoundingClientRect();
      const pct  = clamp((ev.clientX - rect.left) / rect.width, 0, 1);
      if (handle === "start") setTrimStart(clamp(pct, 0, trimEnd - 0.05));
      else                    setTrimEnd(clamp(pct, trimStart + 0.05, 1));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Touch drag (mobile)
  const startTrimTouchDrag = (handle: "start" | "end") => (e: React.TouchEvent) => {
    const track = trimTrackRef.current;
    if (!track) return;
    const onMove = (ev: TouchEvent) => {
      const rect = track.getBoundingClientRect();
      const pct  = clamp((ev.touches[0].clientX - rect.left) / rect.width, 0, 1);
      if (handle === "start") setTrimStart(clamp(pct, 0, trimEnd - 0.05));
      else                    setTrimEnd(clamp(pct, trimStart + 0.05, 1));
    };
    const onUp = () => {
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Reprodução de vídeo
  // ─────────────────────────────────────────────────────────────────────────

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else {
      if (v.currentTime < trimStart * duration || v.currentTime >= trimEnd * duration)
        v.currentTime = trimStart * duration;
      v.play(); setPlaying(true);
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setCurrentTime(v.currentTime);
      if (v.currentTime >= trimEnd * duration) { v.pause(); setPlaying(false); }
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [trimEnd, duration]);

  // ─────────────────────────────────────────────────────────────────────────
  // FFmpeg
  // ─────────────────────────────────────────────────────────────────────────

const loadFFmpeg = async (): Promise<FFmpeg> => {
  if (ffmpegReady.current && ffmpegRef.current) return ffmpegRef.current;

  const ff = new FFmpeg();
  ffmpegRef.current = ff;

  const [coreJS, coreWasm] = await Promise.all([
    fetch("/ffmpeg/ffmpeg-core.js").then((r) => r.blob()),
    fetch("/ffmpeg/ffmpeg-core.wasm").then((r) => r.blob()),
  ]);

  await ff.load({
    coreURL: URL.createObjectURL(new Blob([coreJS],   { type: "text/javascript" })),
    wasmURL: URL.createObjectURL(new Blob([coreWasm], { type: "application/wasm" })),
  });

  ff.on("progress", ({ progress }) =>
    setTrimProgress(Math.round(progress * 100))
  );

  ffmpegReady.current = true;
  return ff;
};

  const trimVideo = async (raw: File): Promise<File> => {
    const startSec = trimStart * duration;
    const durSec   = (trimEnd - trimStart) * duration;
    if (startSec < 0.1 && durSec >= duration - 0.1) return raw;

    setTrimProgress(0);
    const ff = await loadFFmpeg();
    await ff.writeFile("input.mp4", await fetchFile(raw));
    await ff.exec([
      "-ss", startSec.toFixed(3),
      "-i", "input.mp4",
      "-t", durSec.toFixed(3),
      "-c", "copy",
      "-avoid_negative_ts", "make_zero",
      "output.mp4",
    ]);
    const data = await ff.readFile("output.mp4");
    await ff.deleteFile("input.mp4");
    await ff.deleteFile("output.mp4");
    setTrimProgress(null);
    return new File([data], "trimmed.mp4", { type: "video/mp4" });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Exporta o arquivo editado e chama onConfirm
  // ─────────────────────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!file || confirming) return;
    setConfirming(true);
    setError("");

    try {
      let result: File;

      if (mediaType === "image") {
        const canvas = canvasRef.current!;

        // Always load the original image for processing to avoid operating on the cropped preview
        const origImg = await new Promise<HTMLImageElement>((resolve, reject) => {
          const i = new Image();
          i.onload = () => resolve(i);
          i.onerror = reject;
          i.src = imgSrc;
        });

        if (crop && cropped) {
          const pixelCrop = convertToPixelCrop(crop, origImg.naturalWidth, origImg.naturalHeight);
          canvas.width = Math.round(pixelCrop.width);
          canvas.height = Math.round(pixelCrop.height);
          canvas.getContext("2d")!.drawImage(
            origImg,
            pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
            0, 0, canvas.width, canvas.height,
          );
        } else {
          canvas.width  = origImg.naturalWidth;
          canvas.height = origImg.naturalHeight;
          canvas.getContext("2d")!.drawImage(origImg, 0, 0);
        }

        // Aplica filtros/ajustes num canvas offscreen
        const off = document.createElement("canvas");
        off.width  = canvas.width;
        off.height = canvas.height;
        const ctx  = off.getContext("2d")!;
        ctx.filter = adjToCSS(adj, activeFilter.css);
        ctx.drawImage(canvas, 0, 0);

        // Overlay de texto
        if (textValue.trim()) {
          ctx.filter    = "none";
          ctx.font      = `${textBold ? "700" : "400"} ${Math.round(off.width * 0.06)}px sans-serif`;
          ctx.fillStyle = textColor;
          ctx.textAlign = "center";
          ctx.shadowColor   = "rgba(0,0,0,0.6)";
          ctx.shadowBlur    = 6;
          ctx.fillText(textValue, off.width / 2, off.height * 0.88);
        }

        const blob = await new Promise<Blob>((res) =>
          off.toBlob((b) => res(b!), "image/jpeg", 0.92)
        );
        result = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });

      } else {
        result = await trimVideo(file);
      }

      await onConfirm(result);
    } catch (err: any) {
      console.error(err);
      setTrimProgress(null)
      setError(err?.message ?? "Erro ao processar a mídia.");
    } finally {
      setConfirming(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Abas visíveis
  // ─────────────────────────────────────────────────────────────────────────

  const visibleTools: MediaEditorTab[] = tabs
    ?? (mediaType === "video" ? VIDEO_TOOLS : IMAGE_TOOLS);

  const toggleTool = (tool: MediaEditorTab) =>
    setActiveTool(prev => (prev === tool ? null : tool));

  // ─────────────────────────────────────────────────────────────────────────
  // Sub-renders das abas
  // ─────────────────────────────────────────────────────────────────────────

  const renderFilters = () => (
    <div className="me-filters-row">
      {FILTERS.map((f) => (
        <button
          key={f.id}
          className={`me-filter-item${activeFilter.id === f.id ? " active" : ""}`}
          onClick={() => setActiveFilter(f)}
          type="button"
        >
          <div
            className="me-filter-thumb"
            style={{
              backgroundImage: imgSrc ? `url(${imgSrc})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              background: !imgSrc ? f.swatch : undefined,
              filter: f.css === "none" ? undefined : f.css,
            }}
          >
            {activeFilter.id === f.id && <span className="me-check">✓</span>}
          </div>
          <span className="me-filter-label">{f.label}</span>
        </button>
      ))}
    </div>
  );

  const AdjSlider = ({
    label, value, min = 0, max = 200, onChange,
  }: { label: string; value: number; min?: number; max?: number; onChange: (v: number) => void }) => (
    <div className="me-adj-row">
      <span className="me-adj-label">{label}</span>
      <input type="range" min={min} max={max} value={value} className="me-slider"
        onChange={(e) => onChange(+e.target.value)} />
      <span className="me-adj-val">{value}</span>
    </div>
  );

  const renderAdjustments = () => (
    <div className="me-tab-panel">
      <AdjSlider label="Brilho"    value={adj.brightness}  onChange={(v) => setAdj((a) => ({ ...a, brightness: v }))} />
      <AdjSlider label="Contraste" value={adj.contrast}    onChange={(v) => setAdj((a) => ({ ...a, contrast: v }))}   />
      <AdjSlider label={t("mediaEditor.saturation")} value={adj.saturation}  onChange={(v) => setAdj((a) => ({ ...a, saturation: v }))} />
    </div>
  );

  const renderText = () => (
    <div className="me-tab-panel me-text-panel" style={{ paddingBottom: bottomPadding }}> 
      <div className="me-text-tools">
        <button className={`me-tool-btn${textBold ? " active" : ""}`} onClick={() => setTextBold((b) => !b)}><strong>B</strong></button>
        <div className="me-color-dots">
          {TEXT_COLORS.map((c) => (
            <button key={c} className={`me-color-dot${textColor === c ? " active" : ""}`}
              style={{ background: c }} onClick={() => setTextColor(c)} />
          ))}
        </div>
      </div>
      <input className="me-text-input" placeholder="Digite um texto..."
        ref={inputRef} value={textValue} onChange={(e) => setTextValue(e.target.value)} maxLength={80} />
    </div>
  );

  const renderTrim = () => (
    <div className="me-tab-panel">
      <div className="me-trim-times">
        <span>{fmt(trimStart * duration)}</span>
        <span className="me-trim-badge">{fmt((trimEnd - trimStart) * duration)} selecionado</span>
        <span>{fmt(trimEnd * duration)}</span>
      </div>
      <div className="me-trim-track" ref={trimTrackRef}>
        <div className="me-trim-selected" style={{ left: `${trimStart * 100}%`, right: `${(1 - trimEnd) * 100}%` }} />
        <div className="me-waveform">
          {Array.from({ length: 30 }).map((_, i) => (
            <div key={i} className="me-wave-bar"
              style={{ height: `${30 + Math.sin(i * 0.8) * 20 + (i * 7 % 15)}%` }} />
          ))}
        </div>
        <div className="me-trim-handle left" style={{ left: `${trimStart * 100}%` }}
          onMouseDown={startTrimDrag("start")} onTouchStart={startTrimTouchDrag("start")} />
        <div className="me-trim-handle right" style={{ left: `${trimEnd * 100}%` }}
          onMouseDown={startTrimDrag("end")} onTouchStart={startTrimTouchDrag("end")} />
        {duration > 0 && (
          <div className="me-playhead" style={{ left: `${(currentTime / duration) * 100}%` }} />
        )}
      </div>
      <div className="me-trim-controls">
        <button className="me-tool-btn" onClick={() => { if (videoRef.current) videoRef.current.currentTime = trimStart * duration; }}>⏮</button>
        <button className="me-play-btn" onClick={togglePlay}>{playing ? "⏸" : "▶"}</button>
        <button className="me-tool-btn" onClick={() => { if (videoRef.current) videoRef.current.currentTime = trimEnd * duration; }}>⏭</button>
      </div>
    </div>
  );

  const renderAudio = () => (
    <div className="me-tab-panel">
      <AdjSlider label="Volume" value={volume} min={0} max={100}
        onChange={(v) => { setVolume(v); if (videoRef.current) videoRef.current.volume = v / 100; }} />
      <div className="me-adj-row" style={{ marginTop: 6 }}>
        <span className="me-adj-label">Mudo</span>
        <button className={`me-toggle${muted ? " on" : ""}`}
          onClick={() => { setMuted((m) => !m); if (videoRef.current) videoRef.current.muted = !muted; }}>
          <div className="me-toggle-thumb" />
        </button>
        <span className="me-adj-val" style={{ fontSize: 10 }}>{muted ? "Ativo" : "Off"}</span>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Render principal
  // ─────────────────────────────────────────────────────────────────────────

  if (!file) return null;

  const liveFilter = adjToCSS(adj, activeFilter.css);
  const isCropActive = activeTool === ("crop" as any);

  return (
    <div className="me-overlay">
      <div className="me-root">
        {/* ── Header ── */}
        <div className="me-header">
          <button className="me-header-btn" onClick={onCancel} aria-label="Fechar">
            <X size={18} />
          </button>
          <span className="me-header-title">
            {mediaType === "video" ? t("mediaEditor.editVideo") : t("mediaEditor.editImage")}
          </span>

          {/* Toolbar */}
          <div className="me-toolbar">
            {activeTool && (
              <button
                className="me-tool-ok"
                onClick={() => setActiveTool(null)}
                type="button"
              >
                OK
              </button>
            )}
            {mediaType === "image" && (
              <>
                <button type="button" title="Cortar"
                  className={`me-tool-btn${isCropActive ? " active" : ""}`}
                  onClick={() => setActiveTool(isCropActive ? null : "crop" as any)}
                >
                  <Scissors size={16} />
                </button>
                {visibleTools.includes("filtros") && (
                  <button type="button" title="Filtros"
                    className={`me-tool-btn${activeTool === "filtros" ? " active" : ""}`}
                    onClick={() => toggleTool("filtros")}
                  >
                    <Sliders size={16} />
                  </button>
                )}
                {visibleTools.includes("ajustes") && (
                  <button type="button" title="Ajustes"
                    className={`me-tool-btn${activeTool === "ajustes" ? " active" : ""}`}
                    onClick={() => toggleTool("ajustes")}
                  >
                    <SlidersHorizontal size={16} />
                  </button>
                )}
                {visibleTools.includes("texto") && (
                  <button type="button" title="Texto"
                    className={`me-tool-btn${activeTool === "texto" ? " active" : ""}`}
                    onClick={() => toggleTool("texto")}
                  >
                    <Type size={16} />
                  </button>
                )}
              </>
            )}
            {mediaType === "video" && (
              <>
                {visibleTools.includes("trim") && (
                  <button type="button" title="Trim"
                    className={`me-tool-btn${activeTool === "trim" ? " active" : ""}`}
                    onClick={() => toggleTool("trim")}
                  >
                    ✂
                  </button>
                )}
                {visibleTools.includes("audio") && (
                  <button type="button" title={t("mediaEditor.audio")}
                    className={`me-tool-btn${activeTool === "audio" ? " active" : ""}`}
                    onClick={() => toggleTool("audio")}
                  >
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>
                )}
                {visibleTools.includes("texto") && (
                  <button type="button" title="Texto"
                    className={`me-tool-btn${activeTool === "texto" ? " active" : ""}`}
                    onClick={() => toggleTool("texto")}
                  >
                    <Type size={16} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Área de mídia ── */}
        <div className="me-media-area">
          {/* Imagem */}
          {mediaType === "image" && imgSrc && (
            isCropActive ? (
              <ReactCrop
                crop={crop}
                onChange={(_, p) => setCrop(p)}
                onComplete={(_, p) => commitCropPreview(p)}
                aspect={aspectRatio}
                className="react-crop-image"
              >
                <img
                  ref={imgRef}
                  src={imgSrc}
                  onLoad={onCropImageLoad}
                  className="me-img-preview"
                  alt="preview"
                  style={{ filter: liveFilter }}
                />
              </ReactCrop>
            ) : (
              <div className="me-img-wrap">
                <img
                  ref={imgRef}
                  key={cropPreviewSrc || imgSrc}
                  src={cropPreviewSrc || imgSrc}
                  onLoad={onImageLoad}
                  className="me-img-preview"
                  alt="preview"
                  style={{ filter: liveFilter }}
                />
                {textValue && (
                  <div className="me-text-overlay" style={{ color: textColor, fontWeight: textBold ? 700 : 400 }}>
                    {textValue}
                  </div>
                )}
              </div>
            )
          )}

          {/* Vídeo */}
          {mediaType === "video" && vidSrc && (
            <>
              <video ref={videoRef} src={vidSrc} className="me-video-preview"
                playsInline muted={muted}
                onLoadedMetadata={(e) => setDuration((e.target as HTMLVideoElement).duration)}
                onClick={togglePlay}
              />
              {!playing && <button className="me-play-overlay" onClick={togglePlay}>▶</button>}
              <div className="me-duration-badge">
                {fmt(currentTime)} / {fmt((trimEnd - trimStart) * duration)}
              </div>
            </>
          )}
        </div>

        {/* ── Tool panels ── */}
        {activeTool && activeTool !== ("crop" as any) && (
          <div className="me-tool-panel">
            {activeTool === "filtros"  && renderFilters()}
            {activeTool === "ajustes"  && renderAdjustments()}
            {activeTool === "texto"    && renderText()}
            {activeTool === "trim"     && renderTrim()}
            {activeTool === "audio"    && renderAudio()}
          </div>
        )}

        {/* ── Bottom ── */}
        <div className="me-bottom">
          {/* Erro */}
          {error && <p className="me-error">{error}</p>}

          {/* Progresso do FFmpeg */}
          {trimProgress !== null && (
            <div className="me-ffmpeg-wrap">
              <div className="me-ffmpeg-bar" style={{ width: `${trimProgress}%` }} />
              <span className="me-ffmpeg-label">Cortando vídeo… {trimProgress}%</span>
            </div>
          )}

          {/* Rodapé */}
          <div className="me-footer">
            <div className="me-footer-slot">{footer}</div>
            <button className="me-confirm-btn" onClick={handleConfirm} disabled={confirming}>
              {confirming ? <Spinner /> : (confirmLabel ?? <Check size={20} />)}
            </button>
          </div>
        </div>

        {/* Canvas oculto */}
        <canvas ref={canvasRef} className="d-none" />
      </div>
    </div>
  );
};

export default MediaEditor;